import React from "react";
import { Check, Star } from "lucide-react";
import { pricingPlans } from "../data/mock";

const PricingSection = ({ onOpenForm }) => {
  return (
    <section id="pricing" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Pricing Plans
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Transparent Pricing for Every Need
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose the trademark registration package that best fits your business needs and budget. 
            All plans include expert legal guidance and USPTO filing assistance. No hidden fees – 
            what you see is what you pay.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col ${
                plan.recommended ? "ring-4 ring-[#d4af37] lg:scale-105" : "border border-gray-200"
              }`}
            >
              {/* Recommended Badge */}
              {plan.recommended && (
                <div className="absolute top-0 right-0 bg-gradient-secondary text-[#1e3a8a] px-4 py-1 text-sm font-bold rounded-bl-lg flex items-center space-x-1">
                  <Star className="w-4 h-4 fill-current" />
                  <span>RECOMMENDED</span>
                </div>
              )}

              {/* Plan Header */}
              <div className={`p-6 ${plan.recommended ? "bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6]" : "bg-gray-50"}`}>
                <h3 className={`text-2xl font-bold mb-2 ${plan.recommended ? "text-white" : "text-gray-900"}`}>
                  {plan.name}
                </h3>
                <div className={`text-4xl font-bold mb-1 ${plan.recommended ? "text-white" : "text-[#1e3a8a]"}`}>
                  {plan.price}
                </div>
                <div className={`text-sm ${plan.recommended ? "text-white/90" : "text-gray-600"}`}>
                  {plan.fee}
                </div>
                <p className={`text-xs mt-2 ${plan.recommended ? "text-white/80" : "text-gray-500"}`}>
                  {plan.note}
                </p>
              </div>

              {/* Features List - Scrollable */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar" style={{ maxHeight: "320px" }}>
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.recommended ? "text-[#d4af37]" : "text-[#1e3a8a]"
                      }`} />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={onOpenForm}
                  className={`w-full mt-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    plan.recommended
                      ? "bg-[#d4af37] text-white hover:bg-[#f59e0b] shadow-lg hover:shadow-xl"
                      : "bg-[#1e3a8a] text-white hover:bg-[#1e40af]"
                  }`}
                >
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Understanding Trademark Registration Costs
            </h3>
            
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong>Government Filing Fees:</strong> In addition to our service fees, the USPTO charges a 
                government filing fee that ranges from $250 to $350 per class of goods or services. Most trademark 
                applications cover one class, but if your business operates in multiple categories, additional 
                classes may be required.
              </p>
              
              <p>
                <strong>What's Included:</strong> Our service fees cover comprehensive trademark search and analysis, 
                preparation and filing of your trademark application, correspondence with the USPTO, response to 
                office actions (if needed), and monitoring of your application status throughout the entire process.
              </p>
              
              <p>
                <strong>Money-Back Guarantee:</strong> We stand behind our services with a satisfaction guarantee. 
                If we determine during our initial search that your desired trademark is not registrable, we'll 
                provide you with a full refund before any filing is made.
              </p>
              
              <p>
                <strong>Package Recommendations:</strong> For most businesses, we recommend our Deluxe package, 
                which provides comprehensive protection including trademark monitoring, complete documentation, 
                and a dedicated case manager. This package offers the best value and peace of mind for your trademark 
                investment.
              </p>
            </div>
          </div>
        </div>

        {/* Trust Signals */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Trusted by over 15,000 businesses nationwide</p>
          <div className="flex flex-wrap justify-center gap-8 items-center text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-[#d4af37]" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-[#d4af37]" />
              <span>No Hidden Fees</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-[#d4af37]" />
              <span>Money-Back Guarantee</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-[#d4af37]" />
              <span>Licensed Attorneys</span>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </section>
  );
};

export default PricingSection;
